import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Th, Td, IconBtn, Pagination } from "./AdminUsers";
import { Input } from "@/components/ui/input";
import { Search, Ban, RotateCcw, Flag, FlagOff } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["", "Experience", "Conduct", "Internship", "Volunteer", "Training", "Achievement", "Recommendation", "Custom"];

export default function AdminCertificates() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [certType, setCertType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/certificates", { params: { q, cert_type: certType, status_filter: statusFilter, skip, limit } });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {}
  }, [q, certType, statusFilter, skip]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, payload) => {
    try {
      await api.post(`/admin/certificates/${id}/action`, payload);
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout subtitle="Module 5" title="Certificate Management" actions={<div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} certificates</div>}>
      <div className="flex flex-wrap gap-3 mb-5" data-testid="certs-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, no, recipient, issuer…" className="rounded-sm h-9 pl-9 bg-white/5 border-white/10 text-white" data-testid="certs-search" />
        </div>
        <select value={certType} onChange={(e) => setCertType(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="certs-type-filter">
          {TYPES.map((t) => <option key={t} value={t} className="bg-ec-ink">{t || "All types"}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="certs-status-filter">
          <option value="" className="bg-ec-ink">All statuses</option>
          <option value="active" className="bg-ec-ink">Active</option>
          <option value="revoked" className="bg-ec-ink">Revoked</option>
          <option value="flagged" className="bg-ec-ink">Flagged</option>
        </select>
      </div>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="certs-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <Th>No.</Th>
                <Th>Type</Th>
                <Th>Title</Th>
                <Th>Recipient</Th>
                <Th>Issuer</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`cert-row-${c.id}`}>
                  <Td className="font-mono-plex text-xs">{c.certificate_no}</Td>
                  <Td><Badge className="bg-white/10 border-white/10 rounded-sm">{c.cert_type}</Badge></Td>
                  <Td className="max-w-[240px] truncate">{c.title}</Td>
                  <Td>
                    <div className="text-xs">{c.recipient_name}</div>
                    <div className="text-[10px] font-mono-plex text-zinc-500">{c.recipient_ethic_id}</div>
                  </Td>
                  <Td className="text-xs">{c.issuer_org}</Td>
                  <Td>
                    {c.revoked
                      ? <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Revoked</Badge>
                      : c.flagged
                        ? <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 rounded-sm">Flagged</Badge>
                        : <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm">Active</Badge>}
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {c.revoked ? (
                        <IconBtn title="Restore" onClick={() => act(c.id, { action: "restore" })} testid={`cert-restore-${c.id}`}>
                          <RotateCcw className="w-3 h-3" />
                        </IconBtn>
                      ) : (
                        <IconBtn title="Revoke" danger onClick={() => {
                          const reason = prompt("Reason for revocation:") || "";
                          act(c.id, { action: "revoke", reason });
                        }} testid={`cert-revoke-${c.id}`}>
                          <Ban className="w-3 h-3" />
                        </IconBtn>
                      )}
                      {c.flagged ? (
                        <IconBtn title="Unflag" onClick={() => act(c.id, { action: "unflag" })} testid={`cert-unflag-${c.id}`}>
                          <FlagOff className="w-3 h-3" />
                        </IconBtn>
                      ) : (
                        <IconBtn title="Flag" onClick={() => {
                          const reason = prompt("Reason for flagging:") || "";
                          act(c.id, { action: "flag", reason });
                        }} testid={`cert-flag-${c.id}`}>
                          <Flag className="w-3 h-3" />
                        </IconBtn>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-500" data-testid="certs-empty">No certificates.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />
    </AdminLayout>
  );
}
